// ═══════════════════════════════════════════════════════════════
// useFirestore — Hooks para ler/escrever no banco de dados
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import {
  db, storage, processosRef, cobrancasRef, fluxoRef,
  doc, getDocs, addDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy,
  ref, uploadBytes, getDownloadURL
} from "./firebase";

// ── Hook: carrega processos em tempo real ──
export function useProcessos() {
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // onSnapshot = escuta mudanças em tempo real
    // Quando você ou seu sócio alteram algo, atualiza pra todo mundo
    const unsub = onSnapshot(processosRef, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProcessos(data);
      setLoading(false);
    }, (err) => {
      // Erro (rede/permissão): não deixa a tela travada no spinner pra sempre.
      console.error("Erro ao carregar processos:", err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const addProcesso = async (processo) => {
    const docRef = await addDoc(processosRef, {
      ...processo,
      criadoEm: new Date().toISOString(),
      atualizadoEm: new Date().toISOString()
    });
    return docRef.id;
  };

  const updateProcesso = async (id, changes) => {
    const ref = doc(db, "processos", id);
    await updateDoc(ref, { ...changes, atualizadoEm: new Date().toISOString() });
  };

  const deleteProcesso = async (id) => {
    const ref = doc(db, "processos", id);
    await deleteDoc(ref);
  };

  return { processos, loading, addProcesso, updateProcesso, deleteProcesso };
}

// ── Hook: carrega cobranças em tempo real ──
export function useCobrancas(enabled = true) {
  const [cobrancas, setCobrancas] = useState([]);

  useEffect(() => {
    if (!enabled) { setCobrancas([]); return; }
    const unsub = onSnapshot(cobrancasRef, (snapshot) => {
      setCobrancas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error("Erro ao carregar cobranças:", err));
    return () => unsub();
  }, [enabled]);

  const addCobranca = async (cobranca) => {
    return await addDoc(cobrancasRef, { ...cobranca, criadoEm: new Date().toISOString() });
  };

  const updateCobranca = async (id, changes) => {
    await updateDoc(doc(db, "cobrancas", id), changes);
  };

  return { cobrancas, addCobranca, updateCobranca };
}

// ── Hook: carrega fluxo de caixa ──
export function useFluxo(enabled = true) {
  const [fluxo, setFluxo] = useState([]);

  useEffect(() => {
    if (!enabled) { setFluxo([]); return; }
    const unsub = onSnapshot(fluxoRef, (snapshot) => {
      setFluxo(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error("Erro ao carregar fluxo de caixa:", err));
    return () => unsub();
  }, [enabled]);

  const addMovimentacao = async (mov) => {
    return await addDoc(fluxoRef, { ...mov, criadoEm: new Date().toISOString() });
  };

  return { fluxo, addMovimentacao };
}

// ── Upload de arquivos para Firebase Storage ──
export async function uploadArquivo(processoId, tipoDoc, file) {
  const nomeArquivo = `${tipoDoc.replace(/\s/g, "_")}_${Date.now()}_${file.name}`;
  const caminho = `processos/${processoId}/${tipoDoc.replace(/\s/g, "_")}/${nomeArquivo}`;
  const storageRef = ref(storage, caminho);
  
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  
  return {
    nome: file.name,
    tipo: tipoDoc,
    data: new Date().toISOString().split("T")[0],
    url: url,
    caminho: caminho,
    tamanho: file.size
  };
}

// ── Função: popular banco com dados iniciais (rodar 1 vez só) ──
export async function popularDadosIniciais() {
  // Verifica se já tem dados
  const snapshot = await getDocs(processosRef);
  if (snapshot.size > 0) {
    console.log("Banco já tem dados, pulando importação.");
    return false;
  }

  console.log("Populando banco com dados da planilha...");

  const processos = [
    { ait: "C41915555", dtInfracao: "2025-08-12", status: "DEFESA PRÉVIA", dtRecurso: "2025-09-01", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 138, cliente: "David Carlos Pereira Neto", telefone: "", honorario: 1500, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [{ valor: 1500, data: "2025-09-01", obs: "Pgto integral" }], cnh: "Pendente", defesaPrevia: "Pendente", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C41913553", dtInfracao: "2025-08-21", status: "DEFESA PRÉVIA", dtRecurso: "2025-09-16", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 147, cliente: "Fabio de Rezende Tonassi", telefone: "", honorario: 1500, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [{ valor: 1500, data: "2025-09-16", obs: "Pgto integral" }], cnh: "Pendente", defesaPrevia: "Pendente", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C41957142", dtInfracao: "2025-09-26", status: "DEFESA PRÉVIA", dtRecurso: "2025-10-06", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 183, cliente: "Juarez Machado A. de Azevedo", telefone: "", honorario: 1500, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [{ valor: 1500, data: "2025-10-06", obs: "Pgto integral" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "E-16/061/070075/2025", dtInfracao: "2025-11-02", status: "DEFESA PRÉVIA", dtRecurso: "2025-11-26", dtConsulta: "2026-03-18", tipo: "CASSAÇÃO", diasPrescricao: 980, cliente: "Orlando da Costa Soares Jr.", telefone: "(21) 99999-1234", honorario: 2000, formaPgto: "PARCELAMENTO (2X)", saldoPagar: 1000, pagamentos: [{ valor: 1000, data: "2025-11-26", obs: "1ª parcela" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C41989922", dtInfracao: "2025-11-20", status: "DEFESA PRÉVIA", dtRecurso: "2025-12-02", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 238, cliente: "Fabio Graeter", telefone: "", honorario: 1800, formaPgto: "A VISTA", saldoPagar: 300, pagamentos: [{ valor: 1500, data: "2025-12-02", obs: "Pgto parcial" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "CANCELADO", dtInfracao: "2025-11-25", status: "CANCELADO", dtRecurso: "", dtConsulta: "2025-11-25", tipo: "CASSAÇÃO", diasPrescricao: 0, cliente: "Daniella", telefone: "", honorario: 2100, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [], cnh: "Pendente", defesaPrevia: "Pendente", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C42026596", dtInfracao: "2026-01-04", status: "DEFESA PRÉVIA", dtRecurso: "2026-01-12", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 283, cliente: "Anderson Moreira Pinto", telefone: "(21) 98888-5678", honorario: 1500, formaPgto: "PARCELAMENTO (4X)", saldoPagar: 900, pagamentos: [{ valor: 375, data: "2026-01-12", obs: "1ª parcela" }, { valor: 225, data: "2026-02-12", obs: "2ª parcela (parcial)" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C41922691", dtInfracao: "2026-01-04", status: "DEFESA PRÉVIA", dtRecurso: "2026-01-12", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 283, cliente: "Vitor Barbosa da Silva", telefone: "", honorario: 1500, formaPgto: "PARCELAMENTO (2X)", saldoPagar: 600, pagamentos: [{ valor: 900, data: "2026-01-12", obs: "1ª parcela + sinal" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C41999157", dtInfracao: "2026-01-04", status: "DEFESA PRÉVIA", dtRecurso: "2026-01-20", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 283, cliente: "Ana Paula Dias Gomes", telefone: "", honorario: 1800, formaPgto: "PARCELAMENTO (4X)", saldoPagar: 1800, pagamentos: [], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C41912974", dtInfracao: "2025-12-11", status: "DEFESA PRÉVIA", dtRecurso: "2026-01-20", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 259, cliente: "Fabio de Rezende Tonassi", telefone: "", honorario: 1500, formaPgto: "PARCELAMENTO (4X)", saldoPagar: 1500, pagamentos: [], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "C42029823", dtInfracao: "2026-02-08", status: "DEFESA PRÉVIA", dtRecurso: "2026-02-25", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 318, cliente: "Ricardo Alves da S. Leal", telefone: "", honorario: 1400, formaPgto: "A VISTA", saldoPagar: 1400, pagamentos: [], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "E-16/061/002807/2026", dtInfracao: "2026-01-15", status: "DEFESA PRÉVIA", dtRecurso: "2026-02-25", dtConsulta: "2026-03-18", tipo: "SUSPENSÃO", diasPrescricao: 1071, cliente: "Aloysio Maria Teixeira Filho", telefone: "", honorario: 1400, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [{ valor: 1400, data: "2026-02-25", obs: "Pgto integral" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
    { ait: "E-16/061/084636/2025", dtInfracao: "2025-12-11", status: "RECURSO JARI", dtRecurso: "2026-02-25", dtConsulta: "2026-03-18", tipo: "SUSPENSÃO", diasPrescricao: 1071, cliente: "Aloysio Maria Teixeira Filho", telefone: "", honorario: 1400, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [{ valor: 1400, data: "2026-02-25", obs: "Pgto integral" }], cnh: "Pendente", defesaPrevia: "Pendente", jari: "doc", cetran: "Pendente", arquivos: [] },
    { ait: "C41999310", dtInfracao: "2026-02-14", status: "DEFESA PRÉVIA", dtRecurso: "2026-03-10", dtConsulta: "2026-03-18", tipo: "LS", diasPrescricao: 324, cliente: "Raphael D. Rodrigues", telefone: "", honorario: 900, formaPgto: "A VISTA", saldoPagar: 0, pagamentos: [{ valor: 900, data: "2026-03-10", obs: "Pgto integral" }], cnh: "doc", defesaPrevia: "doc", jari: "Pendente", cetran: "Pendente", arquivos: [] },
  ];

  const fluxos = [
    { mes: "Set/25", entradas: 3000, saidas: 450 },
    { mes: "Out/25", entradas: 1500, saidas: 380 },
    { mes: "Nov/25", entradas: 5900, saidas: 720 },
    { mes: "Dez/25", entradas: 1500, saidas: 510 },
    { mes: "Jan/26", entradas: 6300, saidas: 890 },
    { mes: "Fev/26", entradas: 4200, saidas: 640 },
    { mes: "Mar/26", entradas: 900, saidas: 420 },
  ];

  const cobrancas = [
    { cliente: "Orlando da Costa Soares Jr.", valor: 1000, metodo: "PIX", status: "pago", data: "2026-03-15", parcela: "2/2" },
    { cliente: "Anderson Moreira Pinto", valor: 375, metodo: "Boleto", status: "pendente", data: "2026-03-20", parcela: "2/4" },
    { cliente: "Ana Paula Dias Gomes", valor: 450, metodo: "Cartão", status: "atrasado", data: "2026-03-10", parcela: "1/4" },
    { cliente: "Vitor Barbosa da Silva", valor: 300, metodo: "PIX", status: "pendente", data: "2026-03-25", parcela: "2/2" },
    { cliente: "Fabio de Rezende Tonassi", valor: 375, metodo: "Boleto", status: "atrasado", data: "2026-03-05", parcela: "1/4" },
    { cliente: "Ricardo Alves da S. Leal", valor: 1400, metodo: "PIX", status: "pendente", data: "2026-03-28", parcela: "1/1" },
    { cliente: "Fabio Graeter", valor: 300, metodo: "PIX", status: "pendente", data: "2026-03-22", parcela: "1/1" },
  ];

  for (const p of processos) await addDoc(processosRef, p);
  for (const f of fluxos) await addDoc(fluxoRef, f);
  for (const c of cobrancas) await addDoc(cobrancasRef, c);

  console.log("Dados importados com sucesso!");
  return true;
}
